import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VerifyBanner } from "@/components/VerifyBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, Trash2, Ban, Landmark } from "lucide-react";
import { toast } from "sonner";

const MODES = [["shipping", "Spedizione"], ["in_person", "Incontro di persona"]];
const TCGS = [["pokemon", "Pokémon"], ["one_piece", "One Piece"], ["altro", "Altri TCG"]];

function ConnectPayouts() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/connect/status").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "return") {
      toast.success("Rientro da Stripe: aggiorno lo stato del conto…");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(load, 800);
    }
  }, []);
  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/connect/onboarding"); window.location.href = data.url; }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  const ready = s?.payouts_enabled && s?.transfers_active;
  return (
    <div className="rounded-xl border border-[#2A3655] bg-[#141B2D] p-4" data-testid="connect-payouts">
      <h3 className="flex items-center gap-2 font-display font-semibold text-[#F8FAFC]"><Landmark className="h-4 w-4 text-[#2DD4BF]" />Ricevi conguagli sul tuo conto</h3>
      <p className="mt-1 text-sm text-[#94A3B8]">Per ricevere automaticamente il conguaglio in euro dopo uno scambio, collega il tuo conto bancario tramite Stripe (è richiesta la verifica dell'identità).</p>
      {s && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded-md border px-2 py-1 ${ready ? "border-[#10B981]/40 text-[#10B981]" : "border-[#2A3655] text-[#CBD5E1]"}`} data-testid="connect-status">
            {ready ? "Conto collegato e pronto a ricevere" : s.onboarded ? "Configurazione da completare" : "Nessun conto collegato"}
          </span>
        </div>
      )}
      {!ready && <Button onClick={start} disabled={busy} data-testid="connect-onboarding-btn" className="mt-3 bg-[#7C3AED] hover:bg-[#6D28D9]">{busy ? "Apertura Stripe…" : s?.onboarded ? "Completa la configurazione" : "Collega conto bancario"}</Button>}
      <p className="mt-2 text-[10px] text-[#64748B]">Ambiente di test Stripe: usa i dati di test forniti da Stripe. Nessun trasferimento reale finché l'account non viene attivato.</p>
    </div>
  );
}

export default function SettingsPage() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [delOpen, setDelOpen] = useState(false);
  const [pwd, setPwd] = useState("");

  useEffect(() => {
    if (user) setF({ bio: user.bio || "", province: user.province || "", contact_telegram: user.contact_telegram || "", contact_email: user.contact_email || "", preferred_tcgs: user.preferred_tcgs || [], trade_modes: user.trade_modes || [], wishlist_public: !!user.wishlist_public, matching_enabled: user.matching_enabled !== false, accept_cash_adjustment: user.accept_cash_adjustment !== false });
    api.get("/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  }, [user]);

  if (!f) return null;
  const toggle = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const save = async () => { try { await api.put("/settings/profile", f); await refresh(); toast.success("Impostazioni salvate."); } catch (e) { toast.error(apiError(e)); } };

  const exportData = async () => {
    try {
      const { data } = await api.get("/settings/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "omniverse-dati.json"; a.click(); URL.revokeObjectURL(url);
      toast.success("Dati esportati.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const unblock = async (username) => { await api.delete(`/blocks/${username}`); setBlocks((b) => b.filter((x) => x.username !== username)); };

  const deleteAccount = async () => {
    try { await api.post("/settings/delete-account", { password: pwd }); toast.success("Account eliminato."); await logout(); nav("/"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-display text-3xl font-bold text-[#F8FAFC]">Impostazioni e privacy</h1>
      <div className="mt-6"><VerifyBanner /></div>
      <Tabs defaultValue="profile">
        <TabsList className="bg-[#141B2D] border border-[#2A3655]">
          <TabsTrigger value="profile" data-testid="tab-profile" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Profilo</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="tab-privacy" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Privacy</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white">Account</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-4">
          <div><Label className="text-[#CBD5E1]">Biografia</Label><Textarea data-testid="settings-bio" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" /></div>
          <div><Label className="text-[#CBD5E1]">Provincia (facoltativa)</Label><Input data-testid="settings-province" value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" /></div>
          <div>
            <Label className="text-[#CBD5E1]">TCG preferiti</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">{TCGS.map(([v, l]) => <button key={v} onClick={() => setF({ ...f, preferred_tcgs: toggle(f.preferred_tcgs, v) })} className={`rounded-md border px-2 py-1 text-xs ${f.preferred_tcgs.includes(v) ? "border-[#7C3AED] bg-[#7C3AED]/15 text-[#a78bfa]" : "border-[#2A3655] text-[#CBD5E1]"}`}>{l}</button>)}</div>
          </div>
          <div>
            <Label className="text-[#CBD5E1]">Modalità di scambio</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">{MODES.map(([v, l]) => <button key={v} onClick={() => setF({ ...f, trade_modes: toggle(f.trade_modes, v) })} className={`rounded-md border px-2 py-1 text-xs ${f.trade_modes.includes(v) ? "border-[#2DD4BF] bg-[#2DD4BF]/15 text-[#2DD4BF]" : "border-[#2A3655] text-[#CBD5E1]"}`}>{l}</button>)}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-[#CBD5E1]">Username Telegram (per contatto)</Label><Input value={f.contact_telegram} onChange={(e) => setF({ ...f, contact_telegram: e.target.value })} placeholder="@iltuousername" data-testid="settings-contact-telegram" className="bg-[#0B1020] border-[#2A3655]" /></div>
            <div><Label className="text-[#CBD5E1]">Email di contatto (facoltativa)</Label><Input value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} placeholder="diversa dall'email di accesso" className="bg-[#0B1020] border-[#2A3655]" /></div>
          </div>
          <div className="rounded-xl border border-[#2A3655] bg-[#141B2D] p-4">
            <Label className="text-[#CBD5E1]">Conguaglio in euro</Label>
            <label className="mt-2 flex items-center justify-between text-sm text-[#CBD5E1]">
              {f.accept_cash_adjustment ? "Accetto anche proposte con conguaglio" : "Solo scambio tra carte (nessun conguaglio)"}
              <Switch checked={f.accept_cash_adjustment} onCheckedChange={(v) => setF({ ...f, accept_cash_adjustment: v })} data-testid="pref-cash" />
            </label>
            <p className="mt-1 text-xs text-[#64748B]">Se lo disattivi, gli altri non potranno inviarti proposte con conguaglio in denaro.</p>
          </div>
          <Button onClick={save} data-testid="save-settings" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Salva</Button>
        </TabsContent>

        <TabsContent value="privacy" className="mt-4 space-y-4">
          <label className="flex items-center justify-between rounded-xl border border-[#2A3655] bg-[#141B2D] p-4 text-sm text-[#CBD5E1]">Wishlist pubblica<Switch checked={f.wishlist_public} onCheckedChange={(v) => setF({ ...f, wishlist_public: v })} data-testid="privacy-wishlist" /></label>
          <label className="flex items-center justify-between rounded-xl border border-[#2A3655] bg-[#141B2D] p-4 text-sm text-[#CBD5E1]">Partecipa al matching<Switch checked={f.matching_enabled} onCheckedChange={(v) => setF({ ...f, matching_enabled: v })} data-testid="privacy-matching" /></label>
          <Button onClick={save} className="bg-[#7C3AED] hover:bg-[#6D28D9]">Salva</Button>
          <div className="rounded-xl border border-[#2A3655] bg-[#141B2D] p-4">
            <h3 className="font-display font-semibold text-[#F8FAFC]">Utenti bloccati</h3>
            {blocks.length === 0 ? <p className="mt-2 text-sm text-[#94A3B8]">Nessun utente bloccato.</p> : (
              <div className="mt-2 space-y-1">{blocks.map((b) => <div key={b.id} className="flex items-center justify-between text-sm text-[#CBD5E1]"><span className="flex items-center gap-1"><Ban className="h-3.5 w-3.5 text-[#EF4444]" />@{b.username}</span><button onClick={() => unblock(b.username)} className="text-xs text-[#2DD4BF] hover:underline">Sblocca</button></div>)}</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="account" className="mt-4 space-y-4">
          <ConnectPayouts />
          <div className="rounded-xl border border-[#2A3655] bg-[#141B2D] p-4">
            <h3 className="font-display font-semibold text-[#F8FAFC]">Esporta i tuoi dati</h3>
            <p className="mt-1 text-sm text-[#94A3B8]">Scarica un file con profilo, carte, wishlist e feedback.</p>
            <Button onClick={exportData} data-testid="export-data" variant="outline" className="mt-3 border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]"><Download className="mr-2 h-4 w-4" />Esporta dati</Button>
          </div>
          <div className="rounded-xl border border-[#EF4444]/40 bg-[#141B2D] p-4">
            <h3 className="font-display font-semibold text-[#EF4444]">Elimina account</h3>
            <p className="mt-1 text-sm text-[#94A3B8]">Rimuove i dati non necessari. Alcune informazioni sugli scambi possono essere conservate per contestazioni o obblighi applicabili. La cancellazione dai backup non è istantanea.</p>
            <Button onClick={() => setDelOpen(true)} data-testid="delete-account" variant="outline" className="mt-3 border-[#EF4444]/40 bg-transparent text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="mr-2 h-4 w-4" />Elimina account</Button>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
          <DialogHeader><DialogTitle className="font-display text-[#EF4444]">Conferma eliminazione</DialogTitle></DialogHeader>
          <p className="text-sm text-[#CBD5E1]">Per confermare, inserisci la tua password.</p>
          <Input type="password" data-testid="delete-password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="bg-[#0B1020] border-[#2A3655]" />
          <DialogFooter><Button onClick={deleteAccount} data-testid="confirm-delete" className="bg-[#EF4444] hover:bg-[#dc2626]">Elimina definitivamente</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
